/**
 * The jump-scare's scream — an original WebAudio synthesis, not a sample.
 *
 * `screamPartials` and `screamEnvelope` are pure (no `AudioContext`, no wall clock)
 * specifically so `test/scare/scream.test.ts` can assert their shape without a browser —
 * `screamPartials` describes 6 inharmonic tonal partials with a rising pitch sweep,
 * `screamEnvelope` describes the master gain's hard-attack/peak/release breakpoints.
 * That envelope is this file's loudness/duration proof: Bun's test runtime has neither
 * `AudioContext` nor `OfflineAudioContext` (empty globals, verified directly against
 * this package's `bun` — there's no polyfill installed and none may be added), so the
 * "render through an OfflineAudioContext" loudness proof isn't reachable here; per the
 * documented fallback, the pure envelope's peak and duration are what's actually tested.
 *
 * `synthScream` is the only impure part: it schedules an `OscillatorNode` per partial —
 * each with a mid-scream pitch break (an instantaneous frequency jump, not a ramp, for
 * the "voice crack" a real scream has) — routed through a small parallel formant-filter
 * bank (fixed bandpass resonances that shape the raw partials into a vowel-like voiced
 * core instead of a flat buzzy tone), plus a filtered, LFO-rasped white-noise burst for
 * the breathy "shock"/rasp component, shaped by `screamEnvelope`'s hard attack / release
 * envelope, and tears every node down again once the sound has finished playing. See
 * `public/scare/ASSETS.md` for the provenance note.
 */

export interface ScreamPartial {
  readonly freqHz: number;
  readonly gain: number;
  /** Hz added to `freqHz` by the end of the scream — always positive (a rising sweep). */
  readonly sweep: number;
}

const PARTIAL_COUNT = 6;
const BASE_HZ = 220;
const MIN_HZ = 180;
const MAX_HZ = 2400;
const NOMINAL_DURATION_S = 1.5;

// Deliberately non-integer multiples of the base frequency — a harmonic
// series (1, 2, 3, 4, ...) would sound like a musical tone, not a scream.
const INHARMONIC_RATIOS: readonly number[] = [1, 1.83, 2.71, 3.62, 4.9, 6.35, 7.9];

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Pure and deterministic: the same `durationS` always produces the same
 * partials, which is what makes it testable and reusable by `synthScream`
 * without the two ever drifting apart.
 */
export function screamPartials(durationS: number): ScreamPartial[] {
  const duration = Number.isFinite(durationS) && durationS > 0 ? durationS : NOMINAL_DURATION_S;
  const durationFactor = clamp(duration / NOMINAL_DURATION_S, 0.5, 2.5);

  return INHARMONIC_RATIOS.slice(0, PARTIAL_COUNT).map((ratio, index) => {
    const freqHz = clamp(BASE_HZ * ratio, MIN_HZ, MAX_HZ);
    const gain = 1 / (index + 1.4);
    const sweep = freqHz * (0.3 + index * 0.04) * durationFactor;
    return { freqHz, gain, sweep };
  });
}

export interface SynthScreamOptions {
  readonly durationS?: number;
  readonly gain?: number;
}

/** ~1.2-1.8s scream — matches the "actual scream" brief, not a stinger-length blip. */
const DEFAULT_DURATION_S = 1.4;
/** Near full scale (was 0.6) — "peak near full scale" is a deliberate loudness choice
 * for a jump-scare payoff, not a mixing-headroom default. */
const DEFAULT_PEAK_GAIN = 0.92;
/** A hard attack: full peak inside 8ms, an order of magnitude faster than a normal
 * synth attack. */
const ATTACK_S = 0.008;
const RELEASE_S = 0.3;
const NOISE_FILTER_HZ = 1500;

/** Where in the scream (as a fraction of its total length) the pitch cracks — an
 * instantaneous frequency jump, not a ramp, on every partial simultaneously. */
const PITCH_BREAK_FRACTION = 0.55;
/** The multiplicative frequency jump at the break — a "voice crack" upward, not a
 * continuation of the smooth sweep. */
const PITCH_BREAK_JUMP = 1.4;

/** Fixed formant (vocal-resonance) frequencies the raw partials are filtered through in
 * parallel — roughly a stressed/shouted vowel's F1/F2/F3 — so the voiced core reads as
 * vowel-shaped rather than a flat buzzy tone. */
const FORMANT_FREQS_HZ: readonly number[] = [800, 1800, 2800];
const FORMANT_Q: readonly number[] = [6, 8, 10];
const FORMANT_Q_DEFAULT = 6;

/** The noise layer's amplitude-modulation rate/depth — turns a smooth hiss into a rough
 * "rasp". */
const RASP_LFO_HZ = 45;
const RASP_DEPTH = 0.6;

export interface ScreamEnvelopePoint {
  readonly atS: number;
  readonly gain: number;
}

export type ScreamEnvelope = readonly [
  ScreamEnvelopePoint,
  ScreamEnvelopePoint,
  ScreamEnvelopePoint,
  ScreamEnvelopePoint,
];

/**
 * Pure attack/sustain/release envelope breakpoints for the scream's master gain —
 * factored out of `synthScream` so its shape (hard attack, peak, release) is
 * unit-testable without an `AudioContext`; see this file's header for why that's the
 * actual loudness/duration proof here. `gain` is clamped to 0..1; a non-finite or
 * non-positive `durationS` falls back to `DEFAULT_DURATION_S`, same as `screamPartials`.
 */
export function screamEnvelope(durationS: number, gain: number): ScreamEnvelope {
  const duration = Number.isFinite(durationS) && durationS > 0 ? durationS : DEFAULT_DURATION_S;
  const peak = clamp(gain, 0, 1);
  const total = Math.max(duration, ATTACK_S + RELEASE_S);
  const releaseStart = Math.max(total - RELEASE_S, ATTACK_S);
  return [
    { atS: 0, gain: 0 },
    { atS: ATTACK_S, gain: peak },
    { atS: releaseStart, gain: peak },
    { atS: total, gain: 0 },
  ] as const;
}

/**
 * Schedules a self-cleaning scream on `ctx`. Never throws: a suspended
 * context is nudged with `resume()` (fire-and-forget, exactly the guard
 * `sigmashake-ballz/client/audio/sfx.ts`'s playback layer uses around
 * `AudioContext.state`) but scheduling proceeds either way — a still-
 * suspended context just means the scheduled nodes end up inaudible, not
 * that this function fails.
 */
export function synthScream(ctx: AudioContext, opts?: SynthScreamOptions): void {
  if (ctx.state !== "running") {
    void ctx.resume().catch(() => {
      // Stays suspended — the nodes below still get scheduled; they just
      // won't be audible until (if ever) something else unlocks the context.
    });
  }

  const durationS = opts?.durationS ?? DEFAULT_DURATION_S;
  const peakGain = clamp(opts?.gain ?? DEFAULT_PEAK_GAIN, 0, 1);
  const now = ctx.currentTime;
  const envelope = screamEnvelope(durationS, peakGain);
  const [attackStart, attackEnd, releaseStart, releaseEnd] = envelope;
  const stopAt = now + releaseEnd.atS;
  const breakAt = now + releaseEnd.atS * PITCH_BREAK_FRACTION;

  const master = ctx.createGain();
  master.gain.setValueAtTime(attackStart.gain, now + attackStart.atS);
  master.gain.linearRampToValueAtTime(attackEnd.gain, now + attackEnd.atS);
  master.gain.setValueAtTime(releaseStart.gain, now + releaseStart.atS);
  master.gain.linearRampToValueAtTime(releaseEnd.gain, now + releaseEnd.atS);
  master.connect(ctx.destination);

  // Formant bank: fixed bandpass resonances in parallel, summed back into `master` —
  // shapes the raw inharmonic partials below into a vowel-like voiced core.
  const formants = FORMANT_FREQS_HZ.map((freqHz, i) => {
    const filter = ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.setValueAtTime(freqHz, now);
    filter.Q.setValueAtTime(FORMANT_Q[i] ?? FORMANT_Q_DEFAULT, now);
    filter.connect(master);
    return filter;
  });

  const oscillators: OscillatorNode[] = [];
  const partialGains: GainNode[] = [];
  for (const partial of screamPartials(durationS)) {
    const osc = ctx.createOscillator();
    osc.type = "sawtooth";

    // Smooth sweep up to the break, an INSTANT jump at the break (the "voice crack",
    // not a ramp), then continue sweeping from the jumped frequency to the end.
    const midFreq = partial.freqHz + partial.sweep * PITCH_BREAK_FRACTION;
    const postBreakFreq = clamp(midFreq * PITCH_BREAK_JUMP, MIN_HZ, MAX_HZ * 2);
    const endFreq = postBreakFreq + partial.sweep * (1 - PITCH_BREAK_FRACTION);
    osc.frequency.setValueAtTime(partial.freqHz, now);
    osc.frequency.linearRampToValueAtTime(midFreq, breakAt);
    osc.frequency.setValueAtTime(postBreakFreq, breakAt);
    osc.frequency.linearRampToValueAtTime(endFreq, stopAt);

    const partialGain = ctx.createGain();
    partialGain.gain.setValueAtTime(partial.gain, now);

    osc.connect(partialGain);
    for (const formant of formants) partialGain.connect(formant);
    osc.start(now);
    osc.stop(stopAt);

    oscillators.push(osc);
    partialGains.push(partialGain);
  }

  // Noise rasp: a filtered white-noise burst, amplitude-modulated by a fast LFO so it
  // reads as a rough rasp rather than a smooth hiss.
  const noise = ctx.createBufferSource();
  noise.buffer = whiteNoiseBuffer(ctx, durationS);
  const bandpass = ctx.createBiquadFilter();
  bandpass.type = "bandpass";
  bandpass.frequency.setValueAtTime(NOISE_FILTER_HZ, now);
  bandpass.Q.setValueAtTime(0.8, now);
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(peakGain * 0.5, now);

  const rasp = ctx.createOscillator();
  rasp.type = "sine";
  rasp.frequency.setValueAtTime(RASP_LFO_HZ, now);
  const raspDepth = ctx.createGain();
  raspDepth.gain.setValueAtTime(peakGain * 0.5 * RASP_DEPTH, now);
  rasp.connect(raspDepth);
  raspDepth.connect(noiseGain.gain);

  noise.connect(bandpass);
  bandpass.connect(noiseGain);
  noiseGain.connect(master);
  noise.start(now);
  noise.stop(stopAt);
  rasp.start(now);
  rasp.stop(stopAt);

  noise.onended = () => {
    for (const osc of oscillators) safeDisconnect(osc);
    for (const gainNode of partialGains) safeDisconnect(gainNode);
    for (const formant of formants) safeDisconnect(formant);
    safeDisconnect(noise);
    safeDisconnect(bandpass);
    safeDisconnect(noiseGain);
    safeDisconnect(rasp);
    safeDisconnect(raspDepth);
    safeDisconnect(master);
  };
}

function whiteNoiseBuffer(ctx: AudioContext, durationS: number): AudioBuffer {
  const frames = Math.max(
    1,
    Math.floor(ctx.sampleRate * Math.max(durationS, ATTACK_S + RELEASE_S)),
  );
  const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
  const channel = buffer.getChannelData(0);
  for (let i = 0; i < channel.length; i++) {
    channel[i] = Math.random() * 2 - 1;
  }
  return buffer;
}

function safeDisconnect(node: AudioNode): void {
  try {
    node.disconnect();
  } catch {
    // Already disconnected (or the context tore down first) — nothing to do.
  }
}
